import { normalizeLanguageCode } from '../i18n/languages.js';
import { CONTACT_EMAIL } from '../config/contact.js';
import { legalConfig } from './legalConfig.js';

const LEGAL_LOCALE_COPY = Object.freeze({
  en: Object.freeze({
    dateLocale: 'en-GB',
    updatedLabelPrefix: 'Last updated: ',
    countryAustria: 'Austria',
    legalFormSoleProprietor: 'Sole proprietor',
    impressum: Object.freeze({
      heading: 'Imprint',
      intro: ['This page provides the operator and contact details for cedrum.'],
      operatorSection: 'Operator and contact',
      nameLabel: 'Name',
      addressLabel: 'Postal address',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      optionalSection: 'Additional business details',
      legalFormLabel: 'Legal form',
      registerCourtLabel: 'Register court/authority',
      registerNumberLabel: 'Register number',
      vatIdLabel: 'VAT ID',
      editorialSection: 'Editorial line',
    }),
    privacy: Object.freeze({
      heading: 'Privacy Policy',
      intro: ['This privacy notice explains how cedrum processes personal data on its public website and related services.'],
      controllerSection: 'Controller',
      nameLabel: 'Name',
      addressLabel: 'Postal address',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      websiteVisitsSection: 'Website visits',
      websiteVisitParagraphs: [
        'When you visit the site, we process technical connection data (for example IP address, timestamp, requested URL, browser metadata) to deliver the site, keep it stable, and prevent abuse.',
      ],
      websiteVisitList: [
        'Legal basis: Art. 6(1)(f) GDPR (legitimate interests in secure and reliable operations).',
        'For strictly necessary storage/access on user devices, Austrian § 165(3) TKG 2021 applies.',
        'No optional analytics or marketing trackers are currently enabled before consent.',
      ],
      waitlistSection: 'Waitlist and launch communication',
      waitlistParagraphs: [
        'If you join the waitlist, we process your email address plus technical anti-abuse and consent evidence.',
      ],
      waitlistList: [
        'The flow uses email confirmation (double opt-in).',
        'Legal bases: Art. 6(1)(a) GDPR (consent) and Austrian § 174 TKG 2021 for direct marketing by email.',
      ],
      providersSection: 'Service providers and recipients',
      transfersSection: 'International transfers',
      transfersParagraphs: [
        'Where Cedrum or its service providers transfer personal data to a country outside the EEA, the transfer is based on a lawful transfer mechanism under Chapter V GDPR, such as an adequacy decision or the European Commission’s Standard Contractual Clauses, together with supplementary measures where appropriate.',
        `Details about the safeguard used for a specific transfer can be requested at ${CONTACT_EMAIL}. Current subprocessors are listed at /subprocessors.`,
        'Regional processing posture depends on the relevant deployment, account configuration, and runtime setup.',
      ],
      retentionSection: 'Retention',
      retentionParagraphs: [
        'We retain personal data only as long as necessary for the purposes described in this policy or as long as required by applicable law.',
        'Examples of retention periods used by cedrum at launch:',
      ],
      retentionList: [
        'Account and profile data: for the lifetime of the account, then deletion or anonymization within 30 days after confirmed closure unless legal retention is required.',
        'Project content and exports: for the lifetime of the account or workspace, then deletion from active systems within 30 days after deletion, subject to backup and operational lifecycle constraints documented internally.',
        'Billing, invoicing, and accounting records: retained for 7 years where required by applicable accounting and tax law.',
        'Support requests: up to 24 months after closure of the request.',
        'Security, abuse-prevention, and audit logs: up to 12 months unless a longer retention period is required for a security incident or legal claim.',
        'Consent records: for the duration of the consent state and up to 3 years after change or withdrawal to document compliance decisions.',
        'Marketing and waitlist contact data: until consent is withdrawn or after 24 months of inactivity, whichever comes first.',
      ],
      rightsSection: 'Your rights',
      rightsList: [
        'Access, rectification, erasure, restriction, portability and objection under applicable law.',
        'You may withdraw consent at any time with effect for the future.',
        'You may lodge a complaint with the competent supervisory authority.',
      ],
      contactSection: 'Contact',
      privacyContactLabel: 'Privacy contact',
      supervisoryAuthorityLabel: 'Austrian supervisory authority',
      supervisoryAuthorityName: 'Austrian Data Protection Authority',
    }),
  }),
  de: Object.freeze({
    dateLocale: 'de-AT',
    updatedLabelPrefix: 'Stand: ',
    countryAustria: 'Österreich',
    legalFormSoleProprietor: 'Einzelunternehmer',
    impressum: Object.freeze({
      heading: 'Impressum',
      intro: ['Diese Seite enthält die Anbieter- und Kontaktdaten von cedrum.'],
      operatorSection: 'Anbieter und Kontakt',
      nameLabel: 'Name',
      addressLabel: 'Anschrift',
      emailLabel: 'E-Mail',
      phoneLabel: 'Telefon',
      optionalSection: 'Weitere Unternehmensangaben',
      legalFormLabel: 'Rechtsform',
      registerCourtLabel: 'Firmenbuch-/Registergericht',
      registerNumberLabel: 'Firmenbuch-/Registernummer',
      vatIdLabel: 'UID',
      editorialSection: 'Grundlegende Richtung',
    }),
    privacy: Object.freeze({
      heading: 'Datenschutzerklärung',
      intro: ['Diese Datenschutzerklärung erklärt, wie cedrum personenbezogene Daten auf seiner öffentlichen Website und in verbundenen Diensten verarbeitet.'],
      controllerSection: 'Verantwortlicher',
      nameLabel: 'Name',
      addressLabel: 'Anschrift',
      emailLabel: 'E-Mail',
      phoneLabel: 'Telefon',
      websiteVisitsSection: 'Website-Besuch',
      websiteVisitParagraphs: [
        'Beim Aufruf der Website verarbeiten wir technische Verbindungsdaten (z. B. IP-Adresse, Zeitstempel, angeforderte URL, Browser-Informationen), um die Seite auszuliefern, Stabilität sicherzustellen und Missbrauch zu verhindern.',
      ],
      websiteVisitList: [
        'Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherem und stabilem Betrieb).',
        'Für unbedingt erforderliche Speicherung/den Zugriff auf Endgeräten gilt § 165 Abs. 3 TKG 2021.',
        'Vor einer Einwilligung werden keine optionalen Analyse- oder Marketing-Tracker aktiviert.',
      ],
      waitlistSection: 'Warteliste und Kommunikation zum Produktstart',
      waitlistParagraphs: [
        'Wenn du dich für die Warteliste anmeldest, verarbeiten wir deine E-Mail-Adresse sowie technische Nachweise zur Missbrauchsabwehr und Einwilligungsdokumentation.',
      ],
      waitlistList: [
        'Die Anmeldung erfolgt über ein Bestätigungsverfahren per E-Mail (Double-Opt-in).',
        'Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) sowie § 174 TKG 2021 für elektronische Direktwerbung.',
      ],
      providersSection: 'Eingesetzte Dienstleister und Empfänger',
      transfersSection: 'Internationale Übermittlungen',
      transfersParagraphs: [
        'Soweit cedrum oder eingesetzte Dienstleister personenbezogene Daten in ein Land außerhalb des EWR übermitteln, erfolgt die Übermittlung auf Basis eines zulässigen Übermittlungsmechanismus nach Kapitel V DSGVO, etwa eines Angemessenheitsbeschlusses oder der Standardvertragsklauseln der Europäischen Kommission, gegebenenfalls ergänzt um zusätzliche Schutzmaßnahmen.',
        `Einzelheiten zu den für eine konkrete Übermittlung verwendeten Garantien kannst du unter ${CONTACT_EMAIL} anfragen. Die aktuellen Unterauftragsverarbeiter sind unter /subprocessors aufgeführt.`,
        'Die tatsächliche regionale Verarbeitungs- und Transferlage hängt von der Bereitstellung, der Kontokonfiguration und der Laufzeitkonfiguration ab.',
      ],
      retentionSection: 'Speicherdauer',
      retentionParagraphs: [
        'Wir speichern personenbezogene Daten nur so lange, wie es für die in dieser Erklärung beschriebenen Zwecke erforderlich ist oder wie es das anwendbare Recht verlangt.',
        'Beispiele für Speicherdauern, die cedrum zum Produktstart nutzt:',
      ],
      retentionList: [
        'Konto- und Profildaten: für die Dauer des Kontos; nach bestätigter Schließung Löschung oder Anonymisierung innerhalb von 30 Tagen, sofern keine gesetzliche Aufbewahrungspflicht besteht.',
        'Projektinhalte und Exporte: für die Dauer des Kontos oder Arbeitsbereichs; nach Löschung Entfernung aus aktiven Systemen innerhalb von 30 Tagen, vorbehaltlich intern dokumentierter Backup- und Betriebszyklen.',
        'Abrechnungs-, Rechnungs- und Buchhaltungsunterlagen: 7 Jahre, soweit dies nach anwendbarem Rechnungs- und Steuerrecht erforderlich ist.',
        'Supportanfragen: bis zu 24 Monate nach Abschluss der Anfrage.',
        'Sicherheits-, Missbrauchspräventions- und Audit-Logs: bis zu 12 Monate, sofern nicht wegen eines Sicherheitsvorfalls oder Rechtsanspruchs eine längere Aufbewahrung erforderlich ist.',
        'Einwilligungsnachweise: für die Dauer des jeweiligen Einwilligungsstatus und bis zu 3 Jahre nach Änderung oder Widerruf zur Dokumentation von Compliance-Entscheidungen.',
        'Marketing- und Wartelistenkontaktdaten: bis zum Widerruf der Einwilligung oder nach 24 Monaten ohne Aktivität, je nachdem, was zuerst eintritt.',
      ],
      rightsSection: 'Deine Rechte',
      rightsList: [
        'Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit und Widerspruch nach den gesetzlichen Voraussetzungen.',
        'Eine erteilte Einwilligung kannst du jederzeit mit Wirkung für die Zukunft widerrufen.',
        'Du kannst Beschwerde bei der zuständigen Aufsichtsbehörde einlegen.',
      ],
      contactSection: 'Kontakt',
      privacyContactLabel: 'Datenschutzkontakt',
      supervisoryAuthorityLabel: 'Aufsichtsbehörde (AT)',
      supervisoryAuthorityName: 'Österreichische Datenschutzbehörde',
    }),
  }),
  es: Object.freeze({
    dateLocale: 'es-ES',
    updatedLabelPrefix: 'Última actualización: ',
    countryAustria: 'Austria',
    legalFormSoleProprietor: 'Empresario individual',
    impressum: Object.freeze({
      heading: 'Aviso legal',
      intro: ['Esta página ofrece los datos del titular y de contacto de cedrum.'],
      operatorSection: 'Titular y contacto',
      nameLabel: 'Nombre',
      addressLabel: 'Dirección postal',
      emailLabel: 'Correo electrónico',
      phoneLabel: 'Teléfono',
      optionalSection: 'Datos empresariales adicionales',
      legalFormLabel: 'Forma jurídica',
      registerCourtLabel: 'Registro o autoridad competente',
      registerNumberLabel: 'Número de registro',
      vatIdLabel: 'IVA / NIF-IVA',
      editorialSection: 'Línea editorial',
    }),
    privacy: Object.freeze({
      heading: 'Política de privacidad',
      intro: ['Esta política explica cómo cedrum trata datos personales en su sitio web público y en los servicios relacionados.'],
      controllerSection: 'Responsable del tratamiento',
      nameLabel: 'Nombre',
      addressLabel: 'Dirección postal',
      emailLabel: 'Correo electrónico',
      phoneLabel: 'Teléfono',
      websiteVisitsSection: 'Visitas al sitio web',
      websiteVisitParagraphs: [
        'Cuando visitas el sitio, tratamos datos técnicos de conexión (por ejemplo, dirección IP, marca temporal, URL solicitada y metadatos del navegador) para servir el sitio, mantener su estabilidad y prevenir abusos.',
      ],
      websiteVisitList: [
        'Base jurídica: art. 6.1.f) RGPD (interés legítimo en operaciones seguras y fiables).',
        'Para el almacenamiento o acceso estrictamente necesario en dispositivos de usuario se aplica el § 165(3) TKG 2021 de Austria.',
        'No se activan rastreadores opcionales de analítica o marketing antes del consentimiento.',
      ],
      waitlistSection: 'Lista de espera y comunicaciones de lanzamiento',
      waitlistParagraphs: [
        'Si te apuntas a la lista de espera, tratamos tu dirección de correo junto con evidencias técnicas de consentimiento y medidas antiabuso.',
      ],
      waitlistList: [
        'El proceso utiliza una doble confirmación por correo electrónico.',
        'Bases jurídicas: art. 6.1.a) RGPD (consentimiento) y § 174 TKG 2021 de Austria para marketing directo por correo electrónico.',
      ],
      providersSection: 'Proveedores de servicios y destinatarios',
      transfersSection: 'Transferencias internacionales',
      transfersParagraphs: [
        'Cuando cedrum o sus proveedores de servicios transfieren datos personales a un país situado fuera del EEE, la transferencia se basa en un mecanismo válido de transferencia conforme al capítulo V del RGPD, como una decisión de adecuación o las cláusulas contractuales tipo de la Comisión Europea, junto con medidas complementarias cuando proceda.',
        `Puedes solicitar información sobre la garantía aplicable a una transferencia concreta en ${CONTACT_EMAIL}. Los subencargados actuales se enumeran en /subprocessors.`,
        'La postura regional efectiva de tratamiento y transferencias depende del despliegue, de la configuración de la cuenta y del entorno de ejecución.',
      ],
      retentionSection: 'Conservación',
      retentionParagraphs: [
        'Conservamos los datos personales solo durante el tiempo necesario para los fines descritos en esta política o durante el tiempo exigido por la ley aplicable.',
        'Ejemplos de plazos de conservación que cedrum aplica en el lanzamiento:',
      ],
      retentionList: [
        'Datos de cuenta y perfil: durante la vida de la cuenta y, tras el cierre confirmado, eliminación o anonimización dentro de los 30 días siguientes salvo obligación legal de conservación.',
        'Contenido de proyectos y exportaciones: durante la vida de la cuenta o del espacio de trabajo y, tras su supresión, eliminación de los sistemas activos dentro de 30 días, sujeto a las limitaciones de copias de seguridad y ciclos operativos documentados internamente.',
        'Registros de facturación, facturas y contabilidad: conservación durante 7 años cuando así lo exijan la normativa contable y fiscal aplicable.',
        'Solicitudes de soporte: hasta 24 meses después del cierre de la solicitud.',
        'Registros de seguridad, prevención de abuso y auditoría: hasta 12 meses, salvo que un incidente de seguridad o una reclamación legal exijan un plazo mayor.',
        'Registros de consentimiento: durante el estado del consentimiento y hasta 3 años después de su cambio o retirada para documentar decisiones de cumplimiento.',
        'Datos de contacto de marketing y lista de espera: hasta que se retire el consentimiento o tras 24 meses de inactividad, lo que ocurra primero.',
      ],
      rightsSection: 'Tus derechos',
      rightsList: [
        'Acceso, rectificación, supresión, limitación, portabilidad y oposición conforme a la ley aplicable.',
        'Puedes retirar tu consentimiento en cualquier momento con efectos para el futuro.',
        'Puedes presentar una reclamación ante la autoridad de control competente.',
      ],
      contactSection: 'Contacto',
      privacyContactLabel: 'Contacto en materia de privacidad',
      supervisoryAuthorityLabel: 'Autoridad austríaca de protección de datos',
      supervisoryAuthorityName: 'Autoridad austríaca de protección de datos',
    }),
  }),
  fr: Object.freeze({
    dateLocale: 'fr-FR',
    updatedLabelPrefix: 'Dernière mise à jour : ',
    countryAustria: 'Autriche',
    legalFormSoleProprietor: 'Entreprise individuelle',
    impressum: Object.freeze({
      heading: 'Mentions légales',
      intro: ['Cette page contient les informations sur l’exploitant et les coordonnées de contact de cedrum.'],
      operatorSection: 'Exploitant et contact',
      nameLabel: 'Nom',
      addressLabel: 'Adresse postale',
      emailLabel: 'E-mail',
      phoneLabel: 'Téléphone',
      optionalSection: 'Informations complémentaires sur l’activité',
      legalFormLabel: 'Forme juridique',
      registerCourtLabel: 'Registre ou autorité compétente',
      registerNumberLabel: 'Numéro de registre',
      vatIdLabel: 'Numéro de TVA',
      editorialSection: 'Ligne éditoriale',
    }),
    privacy: Object.freeze({
      heading: 'Politique de confidentialité',
      intro: ['Cette politique explique comment cedrum traite les données personnelles sur son site web public et dans les services associés.'],
      controllerSection: 'Responsable du traitement',
      nameLabel: 'Nom',
      addressLabel: 'Adresse postale',
      emailLabel: 'E-mail',
      phoneLabel: 'Téléphone',
      websiteVisitsSection: 'Visites du site web',
      websiteVisitParagraphs: [
        'Lorsque vous visitez le site, nous traitons des données techniques de connexion (par exemple adresse IP, horodatage, URL demandée et métadonnées du navigateur) afin de fournir le site, d’en assurer la stabilité et de prévenir les abus.',
      ],
      websiteVisitList: [
        'Base juridique : art. 6(1)(f) RGPD (intérêts légitimes liés à des opérations sûres et fiables).',
        'Pour le stockage ou l’accès strictement nécessaire sur les terminaux des utilisateurs, le § 165(3) TKG 2021 autrichien s’applique.',
        'Aucun traceur optionnel d’analyse ou de marketing n’est activé avant le consentement.',
      ],
      waitlistSection: 'Liste d’attente et communications de lancement',
      waitlistParagraphs: [
        'Si vous rejoignez la liste d’attente, nous traitons votre adresse e-mail ainsi que des éléments techniques de preuve du consentement et de prévention des abus.',
      ],
      waitlistList: [
        'Le processus prévoit une double confirmation par e-mail.',
        'Bases juridiques : art. 6(1)(a) RGPD (consentement) et § 174 TKG 2021 autrichien pour la prospection directe par e-mail.',
      ],
      providersSection: 'Prestataires et destinataires',
      transfersSection: 'Transferts internationaux',
      transfersParagraphs: [
        'Lorsque cedrum ou ses prestataires transfèrent des données personnelles vers un pays situé en dehors de l’EEE, le transfert repose sur un mécanisme licite au sens du chapitre V du RGPD, tel qu’une décision d’adéquation ou les clauses contractuelles types de la Commission européenne, complétées le cas échéant par des mesures supplémentaires.',
        `Vous pouvez demander des précisions sur la garantie applicable à un transfert donné à ${CONTACT_EMAIL}. Les sous-traitants ultérieurs actuels sont listés sur /subprocessors.`,
        'La posture régionale effective de traitement et de transfert dépend du déploiement, de la configuration du compte et de l’environnement d’exécution.',
      ],
      retentionSection: 'Conservation',
      retentionParagraphs: [
        'Nous conservons les données personnelles uniquement pendant la durée nécessaire aux finalités décrites dans cette politique ou pendant la durée exigée par le droit applicable.',
        'Exemples de durées de conservation appliquées par cedrum au lancement :',
      ],
      retentionList: [
        'Données de compte et de profil : pendant la durée de vie du compte puis suppression ou anonymisation dans les 30 jours suivant sa fermeture confirmée, sauf obligation légale de conservation.',
        'Contenu des projets et exports : pendant la durée de vie du compte ou de l’espace de travail puis suppression des systèmes actifs dans les 30 jours suivant la suppression, sous réserve des contraintes de sauvegarde et de cycle opérationnel documentées en interne.',
        'Documents de facturation, de comptabilité et pièces associées : conservation pendant 7 ans lorsque le droit comptable ou fiscal applicable l’exige.',
        'Demandes de support : jusqu’à 24 mois après la clôture de la demande.',
        'Journaux de sécurité, de prévention des abus et d’audit : jusqu’à 12 mois, sauf durée plus longue requise en cas d’incident de sécurité ou de réclamation juridique.',
        'Preuves de consentement : pendant toute la durée de l’état de consentement et jusqu’à 3 ans après sa modification ou son retrait afin de documenter les décisions de conformité.',
        'Données de contact marketing et de liste d’attente : jusqu’au retrait du consentement ou après 24 mois d’inactivité, selon la première échéance.',
      ],
      rightsSection: 'Vos droits',
      rightsList: [
        'Accès, rectification, effacement, limitation, portabilité et opposition selon le droit applicable.',
        'Vous pouvez retirer votre consentement à tout moment pour l’avenir.',
        'Vous pouvez introduire une réclamation auprès de l’autorité de contrôle compétente.',
      ],
      contactSection: 'Contact',
      privacyContactLabel: 'Contact pour la protection des données',
      supervisoryAuthorityLabel: 'Autorité autrichienne de protection des données',
      supervisoryAuthorityName: 'Autorité autrichienne de protection des données',
    }),
  }),
});

const PROVIDER_COPY = Object.freeze({
  en: Object.freeze({
    'cloudflare-hosting': Object.freeze({
      role: 'Website hosting, CDN, and technical security for the public site',
      data: 'Connection and security metadata (for example IP address, request metadata, error data)',
      location: 'Global network; processing may also occur outside the EEA',
    }),
    'cloudflare-turnstile': Object.freeze({
      role: 'Bot protection for the waitlist form',
      data: 'Technical verification data submitted with the form',
      location: 'Cloudflare infrastructure (global)',
    }),
    resend: Object.freeze({
      role: 'Transactional email delivery and waitlist contact management',
      data: 'Email address, timestamps, and language-related metadata',
      location: 'Resend infrastructure; depending on configuration, processing may also occur outside the EEA',
    }),
  }),
  de: Object.freeze({
    'cloudflare-hosting': Object.freeze({
      role: 'Hosting, CDN und technische Sicherheit der Website',
      data: 'Verbindungs- und Sicherheitsmetadaten (z. B. IP-Adresse, Anfragemetadaten, Fehlerdaten)',
      location: 'Globales Netzwerk; Verarbeitung kann auch außerhalb des EWR stattfinden',
    }),
    'cloudflare-turnstile': Object.freeze({
      role: 'Bot-Schutz für das Formular der Warteliste',
      data: 'Technische Verifizierungsdaten bei Formularabsendung',
      location: 'Cloudflare-Infrastruktur (global)',
    }),
    resend: Object.freeze({
      role: 'Versand transaktionaler E-Mails und Verwaltung der Wartelistenkontakte',
      data: 'E-Mail-Adresse, Zeitstempel und sprachbezogene Metadaten',
      location: 'Resend-Infrastruktur; je nach Konfiguration auch außerhalb des EWR möglich',
    }),
  }),
  es: Object.freeze({
    'cloudflare-hosting': Object.freeze({
      role: 'Alojamiento del sitio web, CDN y seguridad técnica del sitio público',
      data: 'Metadatos de conexión y seguridad (por ejemplo, dirección IP, metadatos de petición y datos de error)',
      location: 'Red global; el tratamiento también puede producirse fuera del EEE',
    }),
    'cloudflare-turnstile': Object.freeze({
      role: 'Protección anti-bots para el formulario de lista de espera',
      data: 'Datos técnicos de verificación enviados con el formulario',
      location: 'Infraestructura de Cloudflare (global)',
    }),
    resend: Object.freeze({
      role: 'Envío de correos transaccionales y gestión de contactos de la lista de espera',
      data: 'Dirección de correo, marcas temporales y metadatos relacionados con el idioma',
      location: 'Infraestructura de Resend; según la configuración, el tratamiento también puede producirse fuera del EEE',
    }),
  }),
  fr: Object.freeze({
    'cloudflare-hosting': Object.freeze({
      role: 'Hébergement du site web, CDN et sécurité technique du site public',
      data: 'Métadonnées de connexion et de sécurité (par exemple adresse IP, métadonnées de requête et données d’erreur)',
      location: 'Réseau mondial ; le traitement peut également avoir lieu en dehors de l’EEE',
    }),
    'cloudflare-turnstile': Object.freeze({
      role: 'Protection anti-bot du formulaire de liste d’attente',
      data: 'Données techniques de vérification transmises avec le formulaire',
      location: 'Infrastructure Cloudflare (globale)',
    }),
    resend: Object.freeze({
      role: 'Envoi d’e-mails transactionnels et gestion des contacts de liste d’attente',
      data: 'Adresse e-mail, horodatages et métadonnées liées à la langue',
      location: 'Infrastructure Resend ; selon la configuration, le traitement peut également avoir lieu en dehors de l’EEE',
    }),
  }),
});

function normalizeCountryToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/ö/g, 'oe');
}

function normalizeLegalFormToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z]/g, '');
}

function getLocaleCopy(locale) {
  return LEGAL_LOCALE_COPY[resolveLegalLocale(locale)];
}

function localizeCountry(country, locale) {
  const normalized = String(country || '').trim();
  if (!normalized) {
    return null;
  }

  const token = normalizeCountryToken(normalized);
  if (token === 'at' || token === 'austria' || token === 'oesterreich' || token === 'osterreich') {
    return getLocaleCopy(locale).countryAustria;
  }

  return normalized;
}

function localizeLegalForm(legalForm, locale) {
  const normalized = String(legalForm || '').trim();
  if (!normalized) {
    return null;
  }

  const token = normalizeLegalFormToken(normalized);
  if (
    token === 'einzelunternehmer' ||
    token === 'soleproprietor' ||
    token === 'soletrader' ||
    token === 'empresarioindividual' ||
    token === 'entrepriseindividuelle'
  ) {
    return getLocaleCopy(locale).legalFormSoleProprietor;
  }

  return normalized;
}

function formatLastUpdatedLabel(locale) {
  const copy = getLocaleCopy(locale);
  const date = new Date(`${legalConfig.lastUpdatedDate}T00:00:00.000Z`);

  const formatter = new Intl.DateTimeFormat(copy.dateLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return `${copy.updatedLabelPrefix}${formatter.format(date)}`;
}

function asAddressValue(addressLines, locale, country) {
  if (!Array.isArray(addressLines) || addressLines.length === 0) {
    return null;
  }

  const lines = [...addressLines];
  const localizedCountry = localizeCountry(country, locale);
  if (localizedCountry) {
    const expectedCountryToken = normalizeCountryToken(country);
    const countryIndex = lines.findIndex((line) => normalizeCountryToken(line) === expectedCountryToken);

    if (countryIndex >= 0) {
      lines[countryIndex] = localizedCountry;
    } else {
      lines.push(localizedCountry);
    }
  }

  return {
    type: 'lines',
    lines,
  };
}

function asMailtoValue(email) {
  if (!email) {
    return null;
  }

  return {
    type: 'link',
    href: `mailto:${email}`,
    label: email,
  };
}

function asLinkValue(label, href) {
  if (!label || !href) {
    return null;
  }

  return {
    type: 'link',
    href,
    label,
  };
}

function hasRenderableValue(value) {
  if (!value) {
    return false;
  }

  if (typeof value === 'string') {
    return Boolean(value.trim());
  }

  if (value.type === 'link') {
    return Boolean(value.href && value.label);
  }

  if (value.type === 'lines' && Array.isArray(value.lines)) {
    return value.lines.some((line) => typeof line === 'string' && line.trim());
  }

  return false;
}

function hasRenderableBlock(block) {
  if (!block?.type) {
    return false;
  }

  if (block.type === 'facts' && Array.isArray(block.rows)) {
    return block.rows.some((row) => row?.label && hasRenderableValue(row.value));
  }

  if ((block.type === 'paragraphs' || block.type === 'list') && Array.isArray(block.items)) {
    return block.items.some((item) => typeof item === 'string' && item.trim());
  }

  if (block.type === 'providers' && Array.isArray(block.items)) {
    return block.items.some((item) => item?.name && item?.role);
  }

  return false;
}

function compactDocumentSections(sections) {
  return sections.filter((section) => {
    const heading = String(section?.heading || '').trim();
    const blocks = Array.isArray(section?.blocks) ? section.blocks : [];
    return Boolean(heading) && blocks.some((block) => hasRenderableBlock(block));
  });
}

function getWaitlistConsentPurpose(locale) {
  const resolvedLocale = resolveLegalLocale(locale);

  if (resolvedLocale === 'de') {
    return `Einwilligungszweck: E-Mail-Benachrichtigung zum Produktstart von cedrum (Version: ${legalConfig.waitlistConsentVersion}).`;
  }

  if (resolvedLocale === 'es') {
    return `Finalidad del consentimiento: notificación por correo sobre el lanzamiento de cedrum (versión: ${legalConfig.waitlistConsentVersion}).`;
  }

  if (resolvedLocale === 'fr') {
    return `Finalité du consentement : notification par e-mail du lancement de cedrum (version : ${legalConfig.waitlistConsentVersion}).`;
  }

  return `Consent purpose: cedrum launch notification by email (version: ${legalConfig.waitlistConsentVersion}).`;
}

function buildImpressumFacts(locale) {
  const copy = getLocaleCopy(locale).impressum;

  return [
    { label: copy.nameLabel, value: legalConfig.operator.name },
    { label: copy.addressLabel, value: asAddressValue(legalConfig.operator.addressLines, locale, legalConfig.operator.country) },
    { label: copy.emailLabel, value: asMailtoValue(legalConfig.operator.email) },
    { label: copy.phoneLabel, value: legalConfig.operator.phone },
  ];
}

function buildOptionalCompanyFacts(locale) {
  const copy = getLocaleCopy(locale).impressum;

  return [
    { label: copy.legalFormLabel, value: localizeLegalForm(legalConfig.operator.legalForm, locale) },
    { label: copy.registerCourtLabel, value: legalConfig.operator.registerCourt },
    { label: copy.registerNumberLabel, value: legalConfig.operator.registerNumber },
    { label: copy.vatIdLabel, value: legalConfig.operator.vatId },
  ];
}

function buildProviderItems(locale) {
  const localizedProviders = PROVIDER_COPY[resolveLegalLocale(locale)] || PROVIDER_COPY.en;

  return legalConfig.providers
    .filter((provider) => provider.alwaysRelevant)
    .map((provider) => {
      const localized = localizedProviders[provider.id] || {};
      return {
        ...provider,
        role: localized.role || provider.role,
        data: localized.data || provider.data,
        location: localized.location || provider.location,
      };
    });
}

function buildImpressumDocument(locale) {
  const copy = getLocaleCopy(locale).impressum;
  const optionalFacts = buildOptionalCompanyFacts(locale);
  const sections = compactDocumentSections([
    {
      heading: copy.operatorSection,
      blocks: [
        {
          type: 'facts',
          rows: buildImpressumFacts(locale),
        },
      ],
    },
    {
      heading: copy.optionalSection,
      blocks: [
        {
          type: 'facts',
          rows: optionalFacts,
        },
      ],
    },
    {
      heading: copy.editorialSection,
      blocks: [
        {
          type: 'paragraphs',
          items: [legalConfig.mediaDisclosure.purpose],
        },
      ],
    },
  ]);

  return {
    heading: copy.heading,
    updatedLabel: formatLastUpdatedLabel(locale),
    intro: copy.intro,
    sections,
  };
}

function buildPrivacyDocument(locale) {
  const localeCopy = getLocaleCopy(locale);
  const copy = localeCopy.privacy;
  const providers = buildProviderItems(locale);
  const sections = compactDocumentSections([
    {
      heading: copy.controllerSection,
      blocks: [
        {
          type: 'facts',
          rows: [
            { label: copy.nameLabel, value: legalConfig.operator.name },
            { label: copy.addressLabel, value: asAddressValue(legalConfig.operator.addressLines, locale, legalConfig.operator.country) },
            { label: copy.emailLabel, value: asMailtoValue(legalConfig.operator.email) },
            { label: copy.phoneLabel, value: legalConfig.operator.phone },
          ],
        },
      ],
    },
    {
      heading: copy.websiteVisitsSection,
      blocks: [
        {
          type: 'paragraphs',
          items: copy.websiteVisitParagraphs,
        },
        {
          type: 'list',
          items: copy.websiteVisitList,
        },
      ],
    },
    {
      heading: copy.waitlistSection,
      blocks: [
        {
          type: 'paragraphs',
          items: copy.waitlistParagraphs,
        },
        {
          type: 'list',
          items: [
            copy.waitlistList[0],
            getWaitlistConsentPurpose(locale),
            copy.waitlistList[1],
          ],
        },
      ],
    },
    {
      heading: copy.providersSection,
      blocks: [
        {
          type: 'providers',
          items: providers,
        },
      ],
    },
    {
      heading: copy.transfersSection,
      blocks: [
        {
          type: 'paragraphs',
          items: copy.transfersParagraphs,
        },
      ],
    },
    {
      heading: copy.retentionSection,
      blocks: [
        {
          type: 'paragraphs',
          items: copy.retentionParagraphs,
        },
        {
          type: 'list',
          items: copy.retentionList,
        },
      ],
    },
    {
      heading: copy.rightsSection,
      blocks: [
        {
          type: 'list',
          items: copy.rightsList,
        },
      ],
    },
    {
      heading: copy.contactSection,
      blocks: [
        {
          type: 'facts',
          rows: [
            { label: copy.privacyContactLabel, value: asMailtoValue(CONTACT_EMAIL) },
            {
              label: copy.supervisoryAuthorityLabel,
              value: asLinkValue(copy.supervisoryAuthorityName, 'https://www.dsb.gv.at'),
            },
          ],
        },
      ],
    },
  ]);

  return {
    heading: copy.heading,
    updatedLabel: formatLastUpdatedLabel(locale),
    intro: copy.intro,
    sections,
  };
}

function resolveLegalLocale(locale) {
  const normalized = normalizeLanguageCode(locale);
  if (normalized in LEGAL_LOCALE_COPY) {
    return normalized;
  }

  return 'en';
}

export function getLegalDocument(pageSlug, locale = 'en') {
  const resolvedLocale = resolveLegalLocale(locale);

  if (pageSlug === 'impressum') {
    return buildImpressumDocument(resolvedLocale);
  }

  if (pageSlug === 'privacy') {
    return buildPrivacyDocument(resolvedLocale);
  }

  return null;
}
